import { useState, useRef, useEffect } from "react"
import { observer } from "mobx-react"
import classNames from "classnames"
import { useStore } from "@features/common"
import { object, string } from "yup"
import parse from "react-html-parser"
import { LocalStorageFields } from "@features/subscription"
import { useFormik } from "formik"
import { RouterLink } from "@lib/routing"
import { parseTemplate } from "@lib/string"
import { IconSpinner } from "@lib/icons"
import { mergeClasses } from "@lib/css"

import type {
  ChangeEvent,
  ReactNode,
  InputHTMLAttributes,
  ButtonHTMLAttributes,
} from "react"

type SubscriptionFormProps = {
  onSubmit?: () => void
  classes?: {
    container?: string
    title?: string
  }
}

export const SubscriptionForm = observer(function SubscriptionForm(
  props: SubscriptionFormProps,
) {
  const { GlobalModel, SubscriptionModel } = useStore()
  const { classes, onSubmit } = props
  const [forceEnableButton, setForceEnableButton] = useState<boolean>(false)

  const { handleSubmit, handleChange, values, errors, setErrors } = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: SubscriptionSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setErrors, resetForm }) => {
      try {
        await SubscriptionModel.sendConfirmation(values.email)
        resetForm()

        if (onSubmit) {
          onSubmit()
        }
      } catch {
        setErrors({
          email:
            GlobalModel.getTemplate("subscription.input.error.server") ||
            "Unknown error",
        })
        setForceEnableButton(true)
      }
    },
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event)
    localStorage.setItem(LocalStorageFields.subscriptionPopupShown, "1")
    setErrors({})
    setForceEnableButton(false)
  }

  const isLoading = Boolean(SubscriptionModel.loading)
  const buttonDisabled =
    (!forceEnableButton && (!values.email || Boolean(errors.email))) ||
    isLoading

  const defaultTitleClassNames = "font-serif text-xl text-center"
  const titleClassNames = mergeClasses(defaultTitleClassNames, classes?.title)

  return (
    <div
      className={classNames(
        "space-y-4 flex flex-col items-center px-4 tablet-s:px-10 tablet-s:pb-10",
        classes?.container,
      )}
    >
      <h5 className={titleClassNames}>
        {parse(GlobalModel.getTemplate("subscription.title", false))}
      </h5>
      <form
        className="space-y-5 w-full max-w-[534px]"
        onSubmit={handleSubmit}
        data-test-id="subscription"
      >
        <TextField
          id="email"
          value={values.email}
          onChange={onChange}
          error={
            "email" in errors
              ? GlobalModel.getTemplate("subscription.input.error") ||
                "Email validation error"
              : undefined
          }
          maxLength={254}
          placeholder={GlobalModel.getTemplate(
            "subscription.input.placeholder",
          )}
          className="flex-shrink focus:outline-none"
          data-test-id="subscription__input"
        >
          <Button
            type="submit"
            disabled={buttonDisabled}
            data-test-id="subscription__button"
          >
            {isLoading ? (
              <IconSpinner className="animate-spin text-white" />
            ) : (
              <span
                className={classNames("mt-0.5", {
                  hidden: isLoading,
                })}
              >
                {GlobalModel.getTemplate("subscription.input.button")}
              </span>
            )}
          </Button>
        </TextField>
        <p className="mb-0 text-sm text-center text-gray-250">
          {parseTemplate(
            GlobalModel.getTemplate("subscription.agreement"),
            (message, data = "") => (
              <RouterLink
                key={message}
                uri={data}
                target="_blank"
                className="text-black transition-colorstransition-colors duration-100 ease-in hover:text-gray-250"
              >
                <em>{message}</em>
              </RouterLink>
            ),
          )}
        </p>
      </form>
    </div>
  )
})

const SubscriptionSchema = object().shape({
  email: string().email("Invalid email").required("Required"),
})

type TextFieldProps = {
  error?: string
  message?: string
  children?: ReactNode
} & InputHTMLAttributes<HTMLInputElement>

export function TextField(props: TextFieldProps) {
  const {
    onChange,
    value,
    placeholder,
    error,
    message,
    children,
    className,
    type = "text",
    maxLength,
    ...rest
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const inputCopyRef = useRef<HTMLSpanElement>(null)
  const [isFocused, setFocused] = useState<boolean>(false)
  const [isTextOverflow, setTextOverflow] = useState<boolean>(false)

  const handleFocus = () => setFocused(true)
  const handleBlur = () => setFocused(false)

  useEffect(() => {
    const inputComponent = inputRef.current
    const inputCopyComponent = inputCopyRef.current

    if (inputComponent && inputCopyComponent) {
      const inputWidth = inputComponent.getBoundingClientRect().width
      const inputCopyWidth = inputCopyComponent.getBoundingClientRect().width

      if (inputWidth < inputCopyWidth) {
        setTextOverflow(true)
      } else {
        setTextOverflow(false)
      }
    }
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (maxLength && event.target.value.length > maxLength) {
      event.target.value = event.target.value.slice(0, maxLength)
    }
    if (onChange) {
      onChange(event)
    }
  }

  return (
    <div className={className}>
      <div
        className={classNames(
          "relative mx-2 h-15 flex items-center justify-between border-b tablet-s:mx-0",
          {
            "border-b-black": isFocused,
            "border-b-gold-300": !isFocused,
            "border-b-red-300": error,
          },
        )}
      >
        <span
          ref={inputCopyRef}
          className="absolute z-0 top-0 h-0 opacity-0 text-lg select-none pointer-events-none whitespace-nowrap"
        >
          {value}
        </span>
        {isTextOverflow && (
          <div
            className="absolute top-0 left-2 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #fff -0.8%, rgba(255, 255, 255, 0) 12.94%, rgba(255, 255, 255, 0) 78.03%)",
            }}
          />
        )}
        <input
          ref={inputRef}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          placeholder={placeholder}
          type={type}
          className={classNames(
            "mr-2 h-10 flex-grow text-lg border-none placeholder-ga focus:ring-0 focus:outline-none placeholder-gray-200",
            {
              "text-gray-400": !error,
              "text-red-300": error,
            },
          )}
          {...rest}
        />
        {children}
      </div>
      {error || message ? (
        <p className="mb-0 pt-0.5 px-2 min-h-[1.125rem] text-xs text-red-300">
          {error || message}
        </p>
      ) : null}
    </div>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement>

export function Button(props: ButtonProps) {
  const { children, onClick, className, type = "button", ...otherProps } = props

  const defaultClasses =
    "font-sans-bold text-sm text-black border-none disabled:text-gray-200"

  return (
    <button
      className={defaultClasses}
      onClick={onClick}
      type={type}
      {...otherProps}
    >
      {children}
    </button>
  )
}
